import React, { useEffect, useState, ReactElement, useRef, PropsWithChildren, useMemo } from 'react';
import { isNil } from 'lodash';
import { Wrap } from '@fiverr-private/layout_components';
import { findChildByType, excludeChildrenByType, renderChildren } from '@fiverr-private/ui_utils';
import { useElementSize } from '@fiverr-private/hooks';
import { TagProps } from '../Tag/types';
import { TagCollectionProps } from './types';

const TagCollection = ({
  children,
  size,
  variant,
  minHeight,
  height = '100%',
  maxHeight,
  minWidth,
  width = '100%',
  maxWidth,
  gap,
}: PropsWithChildren<TagCollectionProps>) => {
  const showMoreTag = findChildByType(children, 'ShowMoreTag');
  const shouldShowMore = !isNil(showMoreTag);
  const childrenAsTagsArray = useMemo(
    () => excludeChildrenByType(children, 'ShowMoreTag') as ReactElement<TagProps>[],
    [children]
  );

  const ref = useRef<HTMLDivElement>(null);
  const [originalTags, setOriginalTags] = useState<ReactElement<TagProps>[]>([...childrenAsTagsArray]);
  const [tagsToShow, setTagsToShow] = useState<ReactElement<TagProps>[]>([...childrenAsTagsArray]);
  const [hiddenTags, setHiddenTags] = useState<ReactElement<TagProps>[]>([]);
  const [overflowingTagIndex, setOverflowingTagIndex] = useState<number | null>(null);
  const [readyToShowTags, setReadyToShowTags] = useState(false);
  const windowSize = useElementSize(typeof window !== 'undefined' ? window : null);

  useEffect(() => {
    setOverflowingTagIndex(null);
    setReadyToShowTags(false);
    setOriginalTags([...childrenAsTagsArray]);
    setTagsToShow([...childrenAsTagsArray]);
    setHiddenTags([]);
  }, [windowSize, childrenAsTagsArray]);

  // FIRST RENDER - Initial Generation of Tags, Checking which tags overflow (setting overflowingTagIndex)
  useEffect(() => {
    if (ref.current && !overflowingTagIndex) {
      const containerBottomPosition = ref.current.offsetTop + ref.current.offsetHeight;
      const childrenElements = Object.values(ref.current.children) as HTMLElement[];

      for (let i = 0; i < childrenElements.length; i++) {
        const child = childrenElements[i];
        const childBottomPosition = child.offsetTop + child.offsetHeight;
        if (childBottomPosition > containerBottomPosition) {
          setOverflowingTagIndex(i);
          setTagsToShow([...originalTags.slice(0, i)]);
          setHiddenTags([...originalTags.slice(i)]);
          break;
        }
      }
    }
  }, [overflowingTagIndex, originalTags]);

  // SECOND RENDER - if the show more tag is itself overflowing, reduce one more tag
  useEffect(() => {
    if (shouldShowMore && ref.current && !!overflowingTagIndex) {
      const containerBottomPosition = ref.current.offsetTop + ref.current.offsetHeight;

      const childrenElements = Object.values(ref.current.children) as HTMLElement[];
      const showMoreElement = childrenElements[overflowingTagIndex];
      const showMoreElementBottomPosition = showMoreElement.offsetTop + showMoreElement.offsetHeight;

      if (showMoreElementBottomPosition > containerBottomPosition) {
        setOverflowingTagIndex(overflowingTagIndex - 1);
        setTagsToShow([...originalTags.slice(0, overflowingTagIndex - 1)]);
        setHiddenTags([...originalTags.slice(overflowingTagIndex - 1)]);
      } else {
        setReadyToShowTags(true);
      }
    } else if (ref.current) {
      setReadyToShowTags(true);
    }
  }, [shouldShowMore, overflowingTagIndex, originalTags]);

  return (
    <Wrap
      minHeight={minHeight}
      height={height}
      maxHeight={maxHeight}
      minWidth={minWidth}
      width={width}
      maxWidth={maxWidth}
      overflow="hidden"
      alignItems="center"
      alignContent="flexStart"
      visibility={readyToShowTags ? 'visible' : 'hidden'}
      ref={ref}
      gap={gap}
    >
      {tagsToShow.map((tagComponent) =>
        renderChildren(tagComponent, {
          size: size || tagComponent.props.size,
          variant: variant || tagComponent.props.variant,
        })
      )}
      {shouldShowMore &&
        overflowingTagIndex &&
        renderChildren(showMoreTag, {
          hiddenTagsCount: originalTags.length - tagsToShow.length,
          hiddenTags: hiddenTags.map((tagComponent) =>
            renderChildren(tagComponent, {
              size: size || tagComponent.props.size,
              variant: variant || tagComponent.props.variant,
            })
          ),
          variant: variant || (showMoreTag as ReactElement).props.variant,
          size: size || (showMoreTag as ReactElement).props.size,
        })}
    </Wrap>
  );
};

TagCollection.displayName = 'TagCollection';

export default TagCollection;
